"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveDecision } from "@/generated/models/ApiWaveDecision";
import { Time } from "@/helpers/time";
import { FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE, useWaveDecisions } from "@/hooks/waves/useWaveDecisions";
import { getApprovalWaveCloseStatus, getApprovalWindowEndTime, getApprovedDropsCount, hasApprovalDecisionCounts, isApproveWave, type ApprovalWaveCloseStatus } from "@/helpers/waves/approve-wave.helpers";
interface UseApprovalWaveStatusParams {
  readonly wave: ApiWave | null | undefined;
  readonly areDecisionPointsComplete?: boolean | undefined;
  readonly isDecisionPointsLoadError?: boolean | undefined;
  readonly onRetryDecisionPointsLoad?: (() => void) | undefined;
  /**
   * When defined, decision points are owned by the caller and this hook will
   * not start a second full decision-page load for status checks.
   */
  readonly decisionPoints?: readonly ApiWaveDecision[] | undefined;
}
interface ApprovalWaveStatus {
  readonly winningThreshold: number | null;
  readonly winningThresholdMinDurationMs: number | null;
  readonly approvedCount: number | null;
  readonly closeStatus: ApprovalWaveCloseStatus;
  readonly isApprovalStatusLoading: boolean;
  readonly isApprovalStatusError: boolean;
  readonly isVotingClosed: boolean;
  readonly isVotingControlsLocked: boolean;
  readonly retryApprovalStatus: (() => void) | null;
}
const getValidThreshold = (threshold: number | null | undefined) => typeof threshold === "number" && Number.isFinite(threshold) && threshold > 0 ? threshold : null;
const getValidMinDurationMs = (durationMs: number | null | undefined) => typeof durationMs === "number" && Number.isFinite(durationMs) && durationMs >= 0 ? durationMs : null;
const isValidApprovalCount = (value: number | null | undefined): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0;
const MAX_TIMEOUT_MS = 2_147_483_647;
export function useApprovalWaveStatus(t0) {
  const $ = _c(52);
  const {
    areDecisionPointsComplete: t1,
    isDecisionPointsLoadError: t2,
    onRetryDecisionPointsLoad,
    wave,
    decisionPoints
  } = t0;
  const areDecisionPointsComplete = t1 === undefined ? false : t1;
  const isDecisionPointsLoadError = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== wave) {
    t3 = isApproveWave(wave);
    $[0] = wave;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const approveWave = t3;
  const [, setStatusClockTick] = useState(0);
  const currentMillis = Time.currentMillis();
  const hasCallerOwnedDecisionPoints = decisionPoints !== undefined;
  let t4;
  if ($[2] !== approveWave || $[3] !== wave) {
    t4 = approveWave && wave ? getValidThreshold(wave.wave.winning_threshold) : null;
    $[2] = approveWave;
    $[3] = wave;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const winningThreshold = t4;
  let t5;
  if ($[5] !== approveWave || $[6] !== wave) {
    t5 = approveWave && wave ? getValidMinDurationMs(wave.wave.winning_threshold_min_duration_ms) : null;
    $[5] = approveWave;
    $[6] = wave;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const winningThresholdMinDurationMs = t5;
  let t6;
  if ($[8] !== approveWave || $[9] !== areDecisionPointsComplete || $[10] !== decisionPoints || $[11] !== wave) {
    t6 = approveWave && wave ? getApprovedDropsCount({
      areDecisionPointsComplete,
      decisionPoints,
      wave
    }) : 0;
    $[8] = approveWave;
    $[9] = areDecisionPointsComplete;
    $[10] = decisionPoints;
    $[11] = wave;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const providedApprovedCount = t6;
  let t7;
  bb0: {
    if (!approveWave || !wave || areDecisionPointsComplete || hasCallerOwnedDecisionPoints) {
      t7 = false;
      break bb0;
    }
    if (!isValidApprovalCount(wave.wave.max_winners)) {
      t7 = false;
      break bb0;
    }
    if (hasApprovalDecisionCounts(wave)) {
      t7 = false;
      break bb0;
    }
    const endTime = getApprovalWindowEndTime(wave);
    if (endTime !== null && currentMillis >= endTime) {
      t7 = false;
      break bb0;
    }
    t7 = providedApprovedCount === null;
  }
  const shouldLoadCompleteDecisionPoints = t7;
  const t8 = wave?.id ?? "";
  let t9;
  if ($[13] !== shouldLoadCompleteDecisionPoints || $[14] !== t8) {
    t9 = {
      waveId: t8,
      enabled: shouldLoadCompleteDecisionPoints,
      loadAllPages: true,
      pageSize: FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE
    };
    $[13] = shouldLoadCompleteDecisionPoints;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    decisionPoints: loadedDecisionPoints,
    hasLoadedAllPages: hasLoadedCompleteDecisionPoints,
    isLoadingAllPagesError: isInternalDecisionPointsLoadError,
    refetch: retryInternalDecisionPointsLoad,
    fetchNextPage: retryInternalNextDecisionPointsPage,
    hasNextPage: hasInternalNextDecisionPointsPage
  } = useWaveDecisions(t9);
  let t10;
  bb1: {
    if (!approveWave || !wave) {
      t10 = 0;
      break bb1;
    }
    if (providedApprovedCount !== null) {
      t10 = providedApprovedCount;
      break bb1;
    }
    let t11;
    if ($[16] !== hasLoadedCompleteDecisionPoints || $[17] !== loadedDecisionPoints || $[18] !== wave) {
      t11 = getApprovedDropsCount({
        areDecisionPointsComplete: hasLoadedCompleteDecisionPoints,
        decisionPoints: loadedDecisionPoints,
        wave
      });
      $[16] = hasLoadedCompleteDecisionPoints;
      $[17] = loadedDecisionPoints;
      $[18] = wave;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    t10 = t11;
  }
  const approvedCount = t10;
  let t11;
  if ($[20] !== approveWave || $[21] !== approvedCount || $[22] !== wave) {
    t11 = approveWave && wave ? getApprovalWaveCloseStatus({
      approvedCount,
      now: currentMillis,
      wave
    }) : null;
    $[20] = approveWave;
    $[21] = approvedCount;
    $[22] = wave;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const closeStatus = t11;
  let t12;
  if ($[24] !== approveWave || $[25] !== approvedCount || $[26] !== closeStatus || $[27] !== wave) {
    t12 = approveWave && wave && isValidApprovalCount(wave.wave.max_winners) && !isValidApprovalCount(wave.wave.no_of_decisions_left) && approvedCount === null && closeStatus === null;
    $[24] = approveWave;
    $[25] = approvedCount;
    $[26] = closeStatus;
    $[27] = wave;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const isApprovalStatusUnknown = Boolean(t12);
  const isApprovalStatusError = Boolean(isApprovalStatusUnknown && (hasCallerOwnedDecisionPoints && isDecisionPointsLoadError || !hasCallerOwnedDecisionPoints && isInternalDecisionPointsLoadError));
  const isApprovalStatusLoading = isApprovalStatusUnknown && !isApprovalStatusError;
  const isVotingClosed = closeStatus !== null;
  const isVotingControlsLocked = isVotingClosed || isApprovalStatusLoading || isApprovalStatusError;
  let t13;
  if ($[29] !== hasInternalNextDecisionPointsPage || $[30] !== retryInternalDecisionPointsLoad || $[31] !== retryInternalNextDecisionPointsPage) {
    t13 = () => {
      if (hasInternalNextDecisionPointsPage) {
        retryInternalNextDecisionPointsPage();
        return;
      }
      retryInternalDecisionPointsLoad();
    };
    $[29] = hasInternalNextDecisionPointsPage;
    $[30] = retryInternalDecisionPointsLoad;
    $[31] = retryInternalNextDecisionPointsPage;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const retryInternalApprovalStatus = t13;
  let t14;
  bb2: {
    if (!isApprovalStatusError) {
      t14 = null;
      break bb2;
    }
    if (hasCallerOwnedDecisionPoints) {
      t14 = onRetryDecisionPointsLoad ?? null;
      break bb2;
    }
    t14 = retryInternalApprovalStatus;
  }
  const retryApprovalStatus = t14;
  let t15;
  if ($[33] !== approveWave || $[34] !== closeStatus || $[35] !== setStatusClockTick || $[36] !== wave) {
    t15 = () => {
      if (!approveWave || !wave || closeStatus !== null) {
        return;
      }
      const endTime_0 = getApprovalWindowEndTime(wave);
      if (endTime_0 === null) {
        return;
      }
      const delay = Math.min(Math.max(endTime_0 - currentMillis, 0), MAX_TIMEOUT_MS);
      const timeoutId = globalThis.setTimeout(() => {
        setStatusClockTick(_temp);
      }, delay);
      return () => {
        globalThis.clearTimeout(timeoutId);
      };
    };
    $[33] = approveWave;
    $[34] = closeStatus;
    $[35] = setStatusClockTick;
    $[36] = wave;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== approveWave || $[39] !== closeStatus || $[40] !== wave) {
    t16 = [approveWave, closeStatus, currentMillis, wave];
    $[38] = approveWave;
    $[39] = closeStatus;
    $[40] = wave;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  useEffect(t15, t16);
  let t17;
  if ($[42] !== approvedCount || $[43] !== closeStatus || $[44] !== isApprovalStatusError || $[45] !== isApprovalStatusLoading || $[46] !== isVotingClosed || $[47] !== isVotingControlsLocked || $[48] !== retryApprovalStatus || $[49] !== winningThreshold || $[50] !== winningThresholdMinDurationMs) {
    t17 = {
      winningThreshold,
      winningThresholdMinDurationMs,
      approvedCount,
      closeStatus,
      isApprovalStatusLoading,
      isApprovalStatusError,
      isVotingClosed,
      isVotingControlsLocked,
      retryApprovalStatus
    };
    $[42] = approvedCount;
    $[43] = closeStatus;
    $[44] = isApprovalStatusError;
    $[45] = isApprovalStatusLoading;
    $[46] = isVotingClosed;
    $[47] = isVotingControlsLocked;
    $[48] = retryApprovalStatus;
    $[49] = winningThreshold;
    $[50] = winningThresholdMinDurationMs;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  return t17;
}
function _temp(tick) {
  return tick + 1;
}